"use client";

import { useUniversalAuth } from "@west-tennessee-consulting/ui";
// Import from the specific client-portal sub-barrel, NOT the `use-api`
// mega-barrel: the latter's `export *` fan-out forms a require cycle that
// leaves this hook `undefined` under CommonJS/jest (works in the ESM bundle,
// fails in tests). See TESTING_SYSTEMS.
import { useAccessibleClients } from "@/lib/hooks/api/client-portal";

interface UseClientSelfContactReturn {
  /** True when the signed-in user is a client_user. */
  isClientUser: boolean;
  /**
   * The contact id that represents the client themselves — their single
   * directly-granted contact (`grant_scope === "contact"`). Undefined for
   * employees, while loading, or when the client has no single unambiguous
   * direct grant (e.g. company-wide-only access, or multiple direct grants).
   */
  selfContactId: string | undefined;
  /** The display name of that contact, when known. */
  selfContactName: string | undefined;
}

/**
 * Resolves "me" for a client_user filing a form (e.g. creating a ticket).
 *
 * A client is almost always acting on their own behalf, so forcing them to
 * search a contact picker for themselves is illogical. "Me" is the contact
 * directly granted to this user (`grant_scope === "contact"`) — for a
 * single-contact (limited) client that is unambiguously themselves. Company-wide
 * clients may legitimately file for many contacts, so we only treat a SINGLE
 * direct grant as "me" and leave the picker for the rest.
 *
 * Gated to client users via the query's `enabled` flag so employee forms never
 * fetch the accessible-clients payload. The query is shared/cached with the
 * client portal, so this adds no extra request for a client already in-app.
 */
export function useClientSelfContact(enabled: boolean = true): UseClientSelfContactReturn {
  const { isClientUser } = useUniversalAuth();
  const active = enabled && isClientUser;
  const query = useAccessibleClients(active);

  if (!isClientUser || !query.data) {
    return { isClientUser, selfContactId: undefined, selfContactName: undefined };
  }

  const directGrants = query.data.accessibleContacts.filter((c) => c.grant_scope === "contact");

  // Only a SINGLE direct grant is unambiguously "me". Zero (company-wide only)
  // or many (explicitly granted several contacts) → leave the picker.
  if (directGrants.length !== 1) {
    return { isClientUser, selfContactId: undefined, selfContactName: undefined };
  }

  return {
    isClientUser,
    selfContactId: directGrants[0].contact_id,
    selfContactName: directGrants[0].contact_name,
  };
}
